import { IUserBase, IRoleUser } from '@shared/interfaces';

import { ContactUserTypeEnum, DealUserTypeEnum } from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';
import { IRoleAssignmentSelection, ITeamSelection, ITeamTypeSelection } from './roleAssignmentSelectionModel';

export enum TEAM_MEMBER_TYPES {
  Director = 'Director',
  Lieutenant = 'Lieutenant',
  Member = 'Member',
}

export type TTeamMemberType = TEAM_MEMBER_TYPES.Director | TEAM_MEMBER_TYPES.Lieutenant | TEAM_MEMBER_TYPES.Member;

export const allMemberTypes: TTeamMemberType[] = [TEAM_MEMBER_TYPES.Director, TEAM_MEMBER_TYPES.Lieutenant, TEAM_MEMBER_TYPES.Member];

export interface IAssignedUser {
  userType?: ContactUserTypeEnum;
  userId: string;
  isIncluded: boolean;
  contactUserType?: DealUserTypeEnum;
}

export interface IContactAssignedUser {
  userId: string;
  isIncluded: boolean;
  userType?: ContactUserTypeEnum;
}

export interface IAssignedTeamType {
  teamTypeId: string;
  includeFullTeamType?: boolean;
  includeManager?: boolean;
  includedTeamMemberType: TTeamMemberType[];
}

export interface IAssignedTeam {
  teamId: string;
  includeFullTeam?: boolean;
  includedTeamMemberType: TTeamMemberType[];
}

export interface IRoleAssignmentRequest {
  allUsers: boolean;
  assignedUsers: IAssignedUser[];
  assignedTeamTypes: IAssignedTeamType[];
  assignedTeams: IAssignedTeam[];
  includeAllManagers: boolean;
  includeAllDirectors: boolean;
  includeAllLieutenants: boolean;
}

export interface IUpdateRoleAssignment extends IRoleAssignmentRequest {
  roleId: string;
}

export class TeamRequest implements ITeamSelection {
  teamId = '';
  teamName = '';
  includeFullTeam = false;
  includedTeamMemberType: TTeamMemberType[] = [];
  teamDirectors: IUserBase[] = [];
  teamLieutenants: IUserBase[] = [];
  teamMembers: IUserBase[] = [];

  constructor(data?: ITeamSelection, includeFullTeamType?: boolean) {
    if (data) {
      this.teamId = data?.teamId;
      this.teamName = data?.teamName;
      this.includeFullTeam = includeFullTeamType ? false : !!data?.includeFullTeam;
      this.includedTeamMemberType = this.includeFullTeam ? [] : includeFullTeamType ? [] : data?.includedTeamMemberType || [];

      this.teamDirectors = data?.teamDirectors;
      this.teamLieutenants = data?.teamLieutenants;
      this.teamMembers = data?.teamMembers;
    }
  }
}

export class TeamTypeRequest implements Partial<ITeamTypeSelection> {
  teamTypeId = '';
  teamTypeName = '';
  includeFullTeamType = false;
  includeManager = false;
  includedTeamMemberType: TTeamMemberType[] = [];

  constructor(data?: ITeamTypeSelection) {
    if (data) {
      this.teamTypeId = data?.teamTypeId;
      this.teamTypeName = data?.teamTypeName;
      this.includeFullTeamType = !!data?.includeFullTeamType;
      this.includeManager = this.includeFullTeamType ? false : !!data?.includeManager;
      this.includedTeamMemberType = this.includeFullTeamType ? [] : data?.includedTeamMemberType || [];
    }
  }
}

const flattenTeamsAndTeamTypes = (teamTypes: ITeamTypeSelection[] = []) => {
  const assignedTeamTypes: IAssignedTeamType[] = [];
  const assignedTeams: IAssignedTeam[] = [];

  teamTypes.forEach((teamType) => {
    assignedTeamTypes.push(new TeamTypeRequest(teamType));

    teamType?.teams?.forEach((team) => {
      assignedTeams.push(new TeamRequest(team, teamType.includeFullTeamType));
    });
  });

  return {
    assignedTeamTypes,
    assignedTeams,
  };
};

export class RoleAssignmentRequest implements IRoleAssignmentRequest {
  allUsers = true;
  assignedUsers: IAssignedUser[] = [];
  assignedTeamTypes: IAssignedTeamType[] = [];
  assignedTeams: IAssignedTeam[] = [];
  includeAllManagers = false;
  includeAllDirectors = false;
  includeAllLieutenants = false;

  constructor(data: IRoleAssignmentSelection, usersList: (IAssignedUser | IRoleUser)[]) {
    if (data) {
      this.assignedUsers = usersList.map(({ userId, isIncluded }) => ({ userId, isIncluded }));

      if (!data.allUsers) {
        const { assignedTeamTypes, assignedTeams } = flattenTeamsAndTeamTypes(data.teamType);

        this.allUsers = false;

        this.assignedTeamTypes = assignedTeamTypes.filter(
          ({ includeFullTeamType, includeManager, includedTeamMemberType }) => includeFullTeamType || includeManager || includedTeamMemberType.length
        );
        this.assignedTeams = assignedTeams.filter(({ includeFullTeam, includedTeamMemberType }) => includeFullTeam || includedTeamMemberType.length);

        this.includeAllManagers = data.includeAllManagers;
        this.includeAllDirectors = data.includeAllDirectors;
        this.includeAllLieutenants = data.includeAllLieutenants;
      }
    }
  }
}
